import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { generateCustomIdsForList } from '../../../utils/customIdGenerator';
import toast from '../../../utils/toast';

export const useFormSubmissions = () => {
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  
  // Selection state
  const [selectedSubmissions, setSelectedSubmissions] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Detail modal states
  const [showClientDetails, setShowClientDetails] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    form: '',
    client: '',
    submittedBy: '',
    status: '',
    pendingTime: '',
    products: '',
    documents: '',
    verifications: '',
    proformaInvoice: ''
  });

  // Collapsible filter state
  const [showFilters, setShowFilters] = useState(false);

  // Function to calculate pending days left
  const calculatePendingTime = (dueDate) => {
    if (!dueDate) return 'No due date';

    const now = new Date();
    const due = new Date(dueDate);
    const diffInMs = due - now;

    if (diffInMs <= 0) {
      return 'Overdue';
    }

    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    const remainingHours = diffInHours % 24;
    const remainingMinutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ${remainingHours > 0 ? `${remainingHours}h` : ''}`;
    } else if (diffInHours > 0) {
      return `${diffInHours}h ${remainingMinutes > 0 ? `${remainingMinutes}m` : ''}`;
    } else {
      return `${remainingMinutes}m`;
    }
  };

  // Function to get color based on pending time
  const getPendingTimeColor = (dueDate) => {
    if (!dueDate) return 'text-gray-500';

    const now = new Date();
    const due = new Date(dueDate);
    const diffInMs = due - now;
    const diffInHours = diffInMs / (1000 * 60 * 60);

    if (diffInMs <= 0) return 'text-red-600 font-semibold';
    if (diffInHours < 24) return 'text-orange-600 font-semibold';
    if (diffInHours < 72) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Filter function
  const applyFilters = (submissions) => {
    return submissions.filter(submission => {
      const formName = submission.form?.name?.toLowerCase() || '';
      const clientName = submission.masterDataId?.clientName?.toLowerCase() || '';
      const submittedByName = submission.submittedBy?.name?.toLowerCase() || '';
      const submittedByEmail = submission.submittedBy?.email?.toLowerCase() || '';
      const status = submission.status?.toLowerCase() || '';
      const pendingTime = calculatePendingTime(submission.deadlineDate).toLowerCase();
      const products = submission.productsData?.map(p => p.id?.name).join(' ').toLowerCase() || '';
      const documents = `${submission.documentProgress?.uploaded || 0}/${submission.documentProgress?.total || 0}`;
      const verifications = `${submission.verificationProgress?.completed || 0}/${submission.verificationProgress?.total || 0}`;
      const hasProformaInvoice = submission.proformaInvoice ? 'yes' : 'no';

      return (
        formName.includes(filters.form.toLowerCase()) &&
        clientName.includes(filters.client.toLowerCase()) &&
        (submittedByName.includes(filters.submittedBy.toLowerCase()) || 
         submittedByEmail.includes(filters.submittedBy.toLowerCase())) &&
        status.includes(filters.status.toLowerCase()) &&
        pendingTime.includes(filters.pendingTime.toLowerCase()) &&
        products.includes(filters.products.toLowerCase()) &&
        documents.includes(filters.documents.toLowerCase()) &&
        verifications.includes(filters.verifications.toLowerCase()) &&
        hasProformaInvoice.includes(filters.proformaInvoice.toLowerCase())
      );
    });
  };

  // Selection handlers
  const handleSelectSubmission = (submissionId) => {
    const newSelected = new Set(selectedSubmissions);
    if (newSelected.has(submissionId)) {
      newSelected.delete(submissionId);
    } else {
      newSelected.add(submissionId);
    }
    setSelectedSubmissions(newSelected);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSubmissions(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(filteredSubmissions.map(sub => sub._id));
      setSelectedSubmissions(allIds);
      setSelectAll(true);
    }
  };

  const clearSelection = () => {
    setSelectedSubmissions(new Set());
    setSelectAll(false);
  };

  // Filter handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      form: '',
      client: '',
      submittedBy: '',
      status: '',
      pendingTime: '',
      products: '',
      documents: '',
      verifications: '',
      proformaInvoice: ''
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  const getActiveFiltersSummary = () => {
    const activeFilters = [];
    if (filters.form) activeFilters.push(`Form: ${filters.form}`);
    if (filters.client) activeFilters.push(`Client: ${filters.client}`);
    if (filters.submittedBy) activeFilters.push(`Submitted By: ${filters.submittedBy}`);
    if (filters.status) activeFilters.push(`Status: ${filters.status}`);
    if (filters.pendingTime) activeFilters.push(`Pending Time: ${filters.pendingTime}`);
    if (filters.products) activeFilters.push(`Products: ${filters.products}`);
    if (filters.documents) activeFilters.push(`Documents: ${filters.documents}`);
    if (filters.verifications) activeFilters.push(`Verifications: ${filters.verifications}`);
    if (filters.proformaInvoice) activeFilters.push(`Proforma Invoice: ${filters.proformaInvoice}`);
    return activeFilters;
  };

  // Fetch data
  const fetchFormSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/form-submissions');
      const submissionsWithCustomIds = await generateCustomIdsForList('FormSubmission', response.data);
      setFormSubmissions(submissionsWithCustomIds);
    } catch (error) {
      console.error('Failed to fetch form submissions:', error);
      setError('Failed to fetch form submissions');
      toast.error('Failed to fetch form submissions');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters when submissions or filters change
  useEffect(() => {
    setFilteredSubmissions(applyFilters(formSubmissions));
    // Reset selection when filters change
    setSelectedSubmissions(new Set());
    setSelectAll(false);
  }, [formSubmissions, filters]);

  useEffect(() => {
    fetchFormSubmissions();
  }, []);

  return {
    // State
    formSubmissions,
    filteredSubmissions,
    loading,
    error,
    selectedSubmission,
    showDocumentUpload,
    showVerification,
    showHistory,
    showDetails,
    showEdit,
    selectedSubmissions,
    selectAll,
    showClientDetails,
    showUserDetails,
    selectedClient,
    selectedUser,
    filters,
    showFilters,
    
    // Actions
    setSelectedSubmission,
    setShowDocumentUpload,
    setShowVerification,
    setShowHistory,
    setShowDetails,
    setShowEdit,
    setShowClientDetails,
    setShowUserDetails,
    setSelectedClient,
    setSelectedUser,
    setShowFilters,
    setFormSubmissions,
    
    // Handlers
    handleSelectSubmission,
    handleSelectAll,
    clearSelection,
    handleFilterChange,
    clearFilters,
    getActiveFiltersCount,
    getActiveFiltersSummary,
    fetchFormSubmissions,
    
    // Utility functions
    calculatePendingTime,
    getPendingTimeColor,
    applyFilters
  };
}; 